import clsx from 'clsx';
import { Button } from '@/components/Button';
import { Container } from '@/components/Container';
import { DraftMarker } from '@/components/DraftMarker';
import { Eyebrow } from '@/components/Eyebrow';
import { PageHeader } from '@/components/PageHeader';
import { EJOURNAL_NAME, issuesByYear, latestAvailableIssue } from '@/data/ejournal';
import { formatSize } from '@/lib/formatSize';
import type { EJournalIssue } from '@/lib/types';

const FRAME = 'block aspect-[440/570] w-full border border-rule-strong';

// One issue in the archive: the real cover (made from page 1 of the PDF), the
// month, volume and issue, and what the download is. Issues whose PDF has not
// arrived yet show a quiet "coming soon" cover.
function IssueCard({ issue }: { issue: EJournalIssue }) {
  const { pdfUrl, cover, monthName, year, label, pages, sizeBytes } = issue;
  const body = (
    <>
      {cover ? (
        <img
          src={cover}
          alt=""
          width={440}
          height={570}
          loading="lazy"
          decoding="async"
          className={clsx(FRAME, 'bg-img-fallback object-cover', pdfUrl && 'group-hover:border-teal-700')}
        />
      ) : (
        <div
          className={clsx(
            FRAME,
            'flex items-center justify-center bg-sky-50 font-mono text-[0.75rem] tracking-[0.08em] text-muted uppercase',
          )}
        >
          Coming soon
        </div>
      )}
      <p className="mt-3 mb-0 font-serif text-[1.25rem] leading-[1.2] font-semibold text-navy-900 group-hover:text-teal-700">
        {monthName} {year}
      </p>
      <p className="mt-1 mb-0 text-[0.9375rem] text-ink-2">{label}</p>
      {pdfUrl && (
        <p className="mt-1 mb-0 font-mono text-[0.8125rem] text-muted">
          {pages} pages · {formatSize(sizeBytes ?? 0)}
        </p>
      )}
      <p className={clsx('mt-2 mb-0 font-semibold', pdfUrl ? 'text-teal-700 underline underline-offset-[3px]' : 'text-muted')}>
        {pdfUrl ? 'Download PDF ↓' : 'PDF to come'}
      </p>
    </>
  );
  return pdfUrl ? (
    <a
      href={pdfUrl}
      aria-label={`${monthName} ${year}, ${label}, download PDF, ${pages} pages, ${formatSize(sizeBytes ?? 0)}`}
      className="group block no-underline"
    >
      {body}
    </a>
  ) : (
    <div>{body}</div>
  );
}

export function EJournal() {
  const latest = latestAvailableIssue();
  return (
    <>
      <PageHeader
        title={EJOURNAL_NAME}
        intro="A monthly technical journal for students, published by ITES. Each issue is one PDF you can download."
      />
      <Container className="pt-[clamp(40px,6vw,72px)] pb-[clamp(56px,7vw,96px)]">
        {latest?.pdfUrl && (
          <section
            aria-labelledby="latest-issue"
            className="box-content flex flex-wrap gap-x-10 gap-y-6 border border-rule bg-white p-[clamp(20px,3vw,32px)]"
          >
            {latest.cover && (
              <a href={latest.pdfUrl} tabIndex={-1} aria-hidden="true" className="flex-none">
                <img
                  src={latest.cover}
                  alt=""
                  width={440}
                  height={570}
                  className="block h-auto w-[200px] border border-rule-strong"
                />
              </a>
            )}
            <div className="min-w-0 flex-[1_1_320px]">
              <Eyebrow>Latest issue</Eyebrow>
              <h2 id="latest-issue" className="m-0 font-serif text-h2-about font-semibold text-navy-900">
                {latest.monthName} {latest.year}
              </h2>
              <p className="mt-2 mb-0 text-[1.0625rem] text-ink-2">{latest.label}</p>
              <p className="mt-1 mb-0 font-mono text-[0.8125rem] text-muted">
                {latest.pages} pages · {formatSize(latest.sizeBytes ?? 0)}
              </p>
              <Button href={latest.pdfUrl} size="lg" className="mt-5">Download PDF ↓</Button>
              <p className="mt-6 mb-0 max-w-[38em] text-[1rem] leading-[1.6] text-ink-2">
                Every issue carries regular technical series by ITES members that continue from month to month, such
                as Piping System and Networking, together with an editorial, employment news and ITES news. A volume
                runs from March to February.
              </p>
              <DraftMarker className="mt-4">
                DRAFT · description written from reading the issues; ITES to confirm
              </DraftMarker>
            </div>
          </section>
        )}

        {issuesByYear().map((y) => (
          <section key={y.year} aria-label={`${y.year} issues`} className="mt-14">
            <h2 className="m-0 mb-6 font-serif text-[1.875rem] font-semibold text-navy-900">{y.year}</h2>
            <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(min(100%,190px),1fr))] gap-x-6 gap-y-9 p-0">
              {y.items.map((issue) => (
                <li key={issue.id}>
                  <IssueCard issue={issue} />
                </li>
              ))}
            </ul>
          </section>
        ))}
        <DraftMarker className="mt-10">DRAFT · issues marked "coming soon" are still to be received from ITES</DraftMarker>
      </Container>
    </>
  );
}
